import React, { useState } from 'react';

interface GAD7Props {
  onComplete: (answers: GAD7Answers) => void;
}

export interface GAD7Answers {
  question1: string;
  question2: string;
  question3: string;
  question4: string;
  question5: string;
  question6: string;
  question7: string;
  difficultyLevel: string;
}

const GAD7: React.FC<GAD7Props> = ({ onComplete }) => {
  const [showReview, setShowReview] = useState(false);
  const [answers, setAnswers] = useState<GAD7Answers>({
    question1: '',
    question2: '',
    question3: '',
    question4: '',
    question5: '',
    question6: '',
    question7: '',
    difficultyLevel: ''
  });

  const questions = [
    {
      id: 'question1',
      text: 'Feeling nervous, anxious, or on edge.'
    },
    {
      id: 'question2',
      text: 'Not being able to stop or control worrying.'
    },
    {
      id: 'question3',
      text: 'Worrying too much about different things.'
    },
    {
      id: 'question4',
      text: 'Trouble relaxing.'
    },
    {
      id: 'question5',
      text: 'Being so restless that it\'s hard to sit still.'
    },
    {
      id: 'question6',
      text: 'Becoming easily annoyed or irritable.'
    },
    {
      id: 'question7',
      text: 'Feeling afraid as if something awful might happen.'
    }
  ];

  const frequencyOptions = [
    { value: '0', label: 'Not at all' },
    { value: '1', label: 'Several days' },
    { value: '2', label: 'Over half the days' },
    { value: '3', label: 'Nearly every day' }
  ];

  const difficultyOptions = [
    { value: '0', label: 'Not difficult at all' },
    { value: '1', label: 'Somewhat difficult' },
    { value: '2', label: 'Very difficult' },
    { value: '3', label: 'Extremely difficult' }
  ];

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const isAllQuestionsAnswered = () => {
    return Object.values(answers).every(answer => answer !== '');
  };

  const calculateScore = () => {
    const scores = [
      answers.question1,
      answers.question2,
      answers.question3,
      answers.question4,
      answers.question5,
      answers.question6,
      answers.question7
    ];
    return scores.reduce((total, score) => total + parseInt(score || '0'), 0);
  };

  const getScoreInterpretation = (score: number) => {
    if (score >= 0 && score <= 4) return 'Minimal anxiety';
    if (score >= 5 && score <= 9) return 'Mild anxiety';
    if (score >= 10 && score <= 14) return 'Moderate anxiety';
    if (score >= 15 && score <= 21) return 'Severe anxiety';
    return '';
  };

  const handleReviewAndContinue = () => {
    if (isAllQuestionsAnswered()) {
      setShowReview(true);
    }
  };

  const handleSubmit = () => {
    onComplete(answers);
  };

  const getAnswerLabel = (value: string, isFrequency: boolean = true) => {
    const options = isFrequency ? frequencyOptions : difficultyOptions;
    return options.find(option => option.value === value)?.label || '';
  };

  if (showReview) {
    return (
      <div className="app">
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          background: '#28527A',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          marginTop: '2rem'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
              The Real Therapists of Atlanta
            </h1>
            <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
              626 Main Street | Palmetto, GA. 30268 | (470).250.9113
            </p>
            <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
              GAD-7 Review
            </h1>
            <p style={{ color: '#6CA6C1', margin: 0 }}>
              Please review your answers before submitting
            </p>
          </div>

          {/* Review Content */}
          <div style={{
            background: '#113F67',
            borderRadius: '12px',
            border: '1px solid #6CA6C1',
            padding: '2rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>
              Your Responses
            </h3>

            <div style={{ marginBottom: '2rem' }}>
              <p style={{ color: '#d1d5db', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Over the last 2 weeks, how often have you been bothered by the following problems?
              </p>

              {questions.map((question, index) => (
                <div key={question.id} style={{
                  background: '#1f2937',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #374151'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#f9fafb', fontSize: '0.9rem', flex: 1 }}>
                      {index + 1}. {question.text}
                    </span>
                    <span style={{
                      color: '#10b981',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      minWidth: '150px',
                      textAlign: 'right'
                    }}>
                      {getAnswerLabel(answers[question.id as keyof GAD7Answers])}
                    </span>
                  </div>
                </div>
              ))}

              <div style={{
                background: '#1f2937',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                border: '1px solid #374151'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#f9fafb', fontSize: '0.9rem', flex: 1 }}>
                    If you checked off any problems, how difficult have these made it for you to do your work, take care of things at home, or get along with other people?
                  </span>
                  <span style={{
                    color: '#10b981',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    minWidth: '150px',
                    textAlign: 'right'
                  }}>
                    {getAnswerLabel(answers.difficultyLevel, false)}
                  </span>
                </div>
              </div>
            </div>

            {/* Score Summary */}
            <div style={{
              background: '#065f46',
              borderRadius: '8px',
              padding: '1.5rem',
              border: '1px solid #10b981',
              textAlign: 'center'
            }}>
              <h4 style={{ color: '#f9fafb', margin: '0 0 0.5rem 0' }}>
                GAD-7 Score: {calculateScore()}
              </h4>
              <p style={{ color: '#d1d5db', margin: 0, fontSize: '0.875rem' }}>
                {getScoreInterpretation(calculateScore())}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setShowReview(false)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                background: '#374151',
                color: '#f9fafb',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Back to Edit
            </button>

            <button
              onClick={handleSubmit}
              style={{
                background: 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)',
                color: 'white',
                padding: '0.75rem 3rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
              }}
            >
              Submit & Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            The Real Therapists of Atlanta
          </h1>
          <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
            626 Main Street | Palmetto, GA. 30268 | (470).250.9113
          </p>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            GAD-7 Anxiety Scale
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please complete all questions below
          </p>
        </div>

        {/* Question Form */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem' }}>
              <span style={{ color: '#ef4444' }}>*</span> Indicates a required field
            </p>
            <p style={{ color: '#d1d5db', fontSize: '1rem', lineHeight: '1.6', marginBottom: '2rem' }}>
              Over the <strong>last 2 weeks</strong>, how often have you been bothered by the following problems?
            </p>
          </div>

          {/* Questions */}
          {questions.map((question, index) => (
            <div key={question.id} style={{ marginBottom: '2rem' }}>
              <h3 style={{
                color: '#f9fafb',
                fontSize: '1rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'flex-start'
              }}>
                <span style={{ color: '#ef4444', marginRight: '0.25rem' }}>*</span>
                {index + 1}. {question.text}
              </h3>

              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {frequencyOptions.map((option) => (
                  <label key={option.value} style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#d1d5db',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    background: answers[question.id as keyof GAD7Answers] === option.value ? '#1f2937' : '#374151',
                    borderRadius: '8px',
                    border: answers[question.id as keyof GAD7Answers] === option.value ? '2px solid #3b82f6' : '1px solid #4b5563',
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="radio"
                      name={question.id}
                      value={option.value}
                      checked={answers[question.id as keyof GAD7Answers] === option.value}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      style={{ marginRight: '0.75rem', width: '16px', height: '16px' }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {/* Difficulty Question */}
          <div style={{ marginBottom: '2rem', paddingTop: '2rem', borderTop: '1px solid #374151' }}>
            <h3 style={{
              color: '#f9fafb',
              fontSize: '1rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'flex-start'
            }}>
              <span style={{ color: '#ef4444', marginRight: '0.25rem' }}>*</span>
              If you checked off any problems, how difficult have these made it for you to do your work, take care of things at home, or get along with other people?
            </h3>

            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {difficultyOptions.map((option) => (
                <label key={option.value} style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  background: answers.difficultyLevel === option.value ? '#1f2937' : '#374151',
                  borderRadius: '8px',
                  border: answers.difficultyLevel === option.value ? '2px solid #3b82f6' : '1px solid #4b5563',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="radio"
                    name="difficultyLevel"
                    value={option.value}
                    checked={answers.difficultyLevel === option.value}
                    onChange={(e) => handleAnswerChange('difficultyLevel', e.target.value)}
                    style={{ marginRight: '0.75rem', width: '16px', height: '16px' }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleReviewAndContinue}
            disabled={!isAllQuestionsAnswered()}
            style={{
              background: isAllQuestionsAnswered()
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: isAllQuestionsAnswered() ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: isAllQuestionsAnswered()
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none',
              opacity: isAllQuestionsAnswered() ? 1 : 0.6
            }}
          >
            Review & Continue
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress: {Object.values(answers).filter(answer => answer !== '').length} of 8 questions completed
          </div>
        </div>
      </div>
    </div>
  );
};

export default GAD7;